import React, { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import CloseButton from "@/ui/close-button";

type SheetProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  // "sheet" = bottom sheet on mobile / centered panel on desktop.
  // "column" = inline right column on desktop (parent controls layout).
  children: React.ReactNode;
  className?: string;
};

function Sheet({ open, onClose, title, subtitle, children, className }: SheetProps) {
  // Keyboard awareness: a bottom sheet is fixed to the layout-viewport bottom,
  // so an open soft keyboard hides it. Track the visual viewport and lift the
  // sheet above the keyboard, capping its height to the space that remains.
  const [viewport, setViewport] = useState<{ inset: number; height: number }>({
    inset: 0,
    height: 0,
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () =>
      setViewport({
        inset: Math.max(0, window.innerHeight - vv.height - vv.offsetTop),
        height: vv.height,
      });
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [open]);

  if (!open) return null;

  const keyboardOpen = viewport.inset > 60;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end sm:items-center sm:justify-center"
      style={{ paddingBottom: viewport.inset }}
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal
        // While the keyboard is up, cap to the visible height so the top can't
        // scroll off past the notch; otherwise keep the 85vh class.
        style={
          keyboardOpen ? { maxHeight: viewport.height - 24 } : undefined
        }
        className={twMerge(
          "relative w-full max-h-[85vh] overflow-y-auto bg-surface-card border-t border-line-subtle rounded-t-sheet shadow-[0_-18px_50px_rgba(0,0,0,.5)] animate-slide-up",
          "sm:max-w-md sm:rounded-card sm:border sm:animate-none",
          className
        )}
      >
        <div className="sm:hidden mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-line-strong" />
        {(title || subtitle) && (
          <div className="flex items-start justify-between px-5 pt-3 pb-2">
            <div>
              {title && (
                <div className="text-lg font-bold text-ink-primary">{title}</div>
              )}
              {subtitle && (
                <div className="text-xs text-ink-muted">{subtitle}</div>
              )}
            </div>
            <CloseButton onClick={onClose} />
          </div>
        )}
        <div className="px-5 pb-6">{children}</div>
      </div>
    </div>
  );
}

export default Sheet;
